import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getCached, setCached, invalidate, STORES, TTL } from "../../../utils/indexedDBCache";

const BASE_URL = `${process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000'}/api`;

// Two-tier contact cache:
//   Tier 1 — in-memory Map (5 min TTL, session-only, fastest)
//   Tier 2 — IndexedDB (1 hr TTL, survives page reload)
// hoursRemaining is a live WhatsApp session countdown. When loaded from IDB on
// page reload it will be stale, so we serve the cached data immediately then
// kick off a background refresh that overwrites it with the real value.
const contactCache = new Map();
const CONTACT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCachedContact = (contactId) => {
  const entry = contactCache.get(String(contactId));
  if (entry && (Date.now() - entry.timestamp) < CONTACT_CACHE_TTL) {
    return entry.data;
  }
  return null;
};

const setCachedContact = (contactId, data) => {
  contactCache.set(String(contactId), { data, timestamp: Date.now() });
};

// Called by socket handlers when contact data changes in real-time.
// Clears both tiers so the next fetch returns fresh data.
export const invalidateCachedContact = (contactId) => {
  contactCache.delete(String(contactId));
  invalidate(STORES.CONTACTS, String(contactId)); // fire-and-forget IDB delete
};

// Guards against concurrent background refreshes for the same contact.
const contactRefreshInFlight = new Set();

// True single-flight guard for fetchContactById: concurrent callers for the same
// contact id (ChatBody + ChatInfo + useContactSelection can all fire on selection)
// share one in-flight API request and all resolve with its result, instead of the
// previous time-window debounce which made later callers reject with "Debounced".
const contactFetchInFlight = new Map();

const fetchContactFromApi = async (id) => {
  const token = await getAuthToken();
  if (!token) throw new Error("No token found");

  const response = await axios.get(`${BASE_URL}/contacts/getbyid/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  setCachedContact(id, response.data);
  setCached(STORES.CONTACTS, id, response.data, TTL.CONTACTS); // fire-and-forget
  return response.data;
};

// Silent background refresh — updates caches + Redux state (if contact still active).
// Dispatched when fetchContactById returns stale IDB data so hoursRemaining self-corrects.
const refreshContactBackground = createAsyncThunk(
  "inboxInfo/refreshContactBackground",
  async (contactId, { rejectWithValue }) => {
    const id = String(contactId);
    if (contactRefreshInFlight.has(id)) return rejectWithValue("AlreadyRefreshing");
    contactRefreshInFlight.add(id);
    try {
      const token = await getAuthToken();
      if (!token) return rejectWithValue("No token");

      const response = await axios.get(`${BASE_URL}/contacts/getbyid/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCachedContact(id, response.data);
      setCached(STORES.CONTACTS, id, response.data, TTL.CONTACTS);
      return { contactId: id, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    } finally {
      contactRefreshInFlight.delete(id);
    }
  }
);

// Async thunk to fetch inbox info
export const fetchInboxInfo = createAsyncThunk(
  "inboxInfo/fetchInboxInfo",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(`${BASE_URL}/inbox/info`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to fetch contact by ID
// Cache priority: in-memory (5 min) → IndexedDB (1 hr) → API
export const fetchContactById = createAsyncThunk(
  "inboxInfo/fetchContactById",
  async (contactId, { rejectWithValue, dispatch }) => {
    const id = String(contactId);

    // Tier 1: in-memory (fast, session-only)
    const inMemory = getCachedContact(id);
    if (inMemory) return inMemory;

    // Tier 2: IndexedDB (survives reload) — serve stale, refresh hoursRemaining in background
    const fromIDB = await getCached(STORES.CONTACTS, id);
    if (fromIDB) {
      setCachedContact(id, fromIDB);           // warm in-memory so next click is instant
      dispatch(refreshContactBackground(id));  // fire-and-forget — updates hoursRemaining
      return fromIDB;
    }

    // Tier 3: single-flight — concurrent callers share the same in-flight request
    // and all get the real result, rather than later ones being dropped.
    let inFlight = contactFetchInFlight.get(id);
    if (!inFlight) {
      inFlight = fetchContactFromApi(id).finally(() => contactFetchInFlight.delete(id));
      contactFetchInFlight.set(id, inFlight);
    }

    try {
      return await inFlight;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Group analogue of fetchContactById above (WhatsApp Groups plan, Phase G7 follow-up) --
// deliberately without the in-memory/IndexedDB caching tiers: a group's info (subject,
// participant list) changes far less often than a contact's hoursRemaining/status, and this
// keeps the addition isolated from fetchContactById's own multi-tier cache machinery rather
// than threading a group branch through it.
export const fetchGroupById = createAsyncThunk(
  "inboxInfo/fetchGroupById",
  async (groupId, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.get(`${BASE_URL}/whatsapp-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Add one of this account's own saved Contacts to the group as a WhatsApp participant
// (WhatsApp Groups plan follow-up, 2026-09-26). Re-fetches the group afterward rather than
// patching groupInfo.participants locally -- the server already resolved the real participant
// shape (jid/phoneNumberID/name) and isSelfAdmin, cheaper to just ask for the fresh copy than to
// duplicate that shape-building logic here.
export const addGroupParticipant = createAsyncThunk(
  "inboxInfo/addGroupParticipant",
  async ({ groupId, contactId }, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.post(
        `${BASE_URL}/whatsapp-groups/${groupId}/participants`,
        { contactId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(fetchGroupById(groupId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to add member");
    }
  }
);

// Remove an existing group participant (identified by their group-participant JID -- never a
// ContactsModel ref, per this feature's own decision #3).
export const removeGroupParticipant = createAsyncThunk(
  "inboxInfo/removeGroupParticipant",
  async ({ groupId, participantJid }, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.delete(`${BASE_URL}/whatsapp-groups/${groupId}/participants`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { participantJid },
      });
      dispatch(fetchGroupById(groupId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to remove member");
    }
  }
);

// Edit a group's description from the web app (2026-09-26 follow-up). Same re-fetch-rather-
// than-hand-patch pattern as add/removeGroupParticipant above.
export const updateGroupDescription = createAsyncThunk(
  "inboxInfo/updateGroupDescription",
  async ({ groupId, description }, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.put(
        `${BASE_URL}/whatsapp-groups/${groupId}/description`,
        { description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(fetchGroupById(groupId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to update description");
    }
  }
);

// Edit a group's name/subject from the web app (2026-09-26 follow-up). Same shape as
// updateGroupDescription above.
export const updateGroupSubject = createAsyncThunk(
  "inboxInfo/updateGroupSubject",
  async ({ groupId, subject }, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.put(
        `${BASE_URL}/whatsapp-groups/${groupId}/subject`,
        { subject },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(fetchGroupById(groupId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to update group name");
    }
  }
);

// Group admin actions (Tier 3, 2026-09-29): invite link, leave, join-request approval, "who can
// add members". Thin wrappers over /whatsapp-groups/:groupId/* -- none of them change state the
// slice itself stores except member-add mode (re-fetched via fetchGroupById), so no reducers.
const groupActionThunk = (name, method, path, fallbackMsg, { refetch = false } = {}) =>
  createAsyncThunk(`inboxInfo/${name}`, async ({ groupId, body } = {}, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios({
        method,
        url: `${BASE_URL}/whatsapp-groups/${groupId}${path}`,
        data: body,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (refetch) dispatch(fetchGroupById(groupId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message || fallbackMsg);
    }
  });

export const fetchGroupInviteLink = groupActionThunk("fetchGroupInviteLink", "get", "/invite-link", "Failed to get invite link");
export const resetGroupInviteLink = groupActionThunk("resetGroupInviteLink", "post", "/invite-link/revoke", "Failed to reset invite link");
export const leaveWhatsappGroup = groupActionThunk("leaveWhatsappGroup", "post", "/leave", "Failed to leave group");
export const fetchGroupJoinRequests = groupActionThunk("fetchGroupJoinRequests", "get", "/join-requests", "Failed to load join requests");
export const respondGroupJoinRequests = groupActionThunk("respondGroupJoinRequests", "post", "/join-requests", "Failed to update join request", { refetch: true });
export const setGroupMemberAddMode = groupActionThunk("setGroupMemberAddMode", "put", "/member-add-mode", "Failed to update setting", { refetch: true });

// Module-level debounce so simultaneous mounts only fire one request.
const labelsFetchDebounceMap = new Map();
const LABELS_DEBOUNCE_TIME = 2000;
const shouldDebounceLabelsFetch = () => {
  const now = Date.now();
  const last = labelsFetchDebounceMap.get('fetchLabels');
  if (last && (now - last) < LABELS_DEBOUNCE_TIME) return true;
  labelsFetchDebounceMap.set('fetchLabels', now);
  return false;
};

// Clears all module-level debounce/cache maps — call on logout so the next
// account's fetches aren't skipped/served from the previous account's data.
export const __resetInboxInfoCaches = () => {
  contactCache.clear();
  labelsFetchDebounceMap.clear();
};

// Async thunk to fetch labels
export const fetchLabels = createAsyncThunk(
  "inboxInfo/fetchLabels",
  async (_, { rejectWithValue }) => {
    if (shouldDebounceLabelsFetch()) return rejectWithValue("Debounced");

    // Shared IDB cache with labelsSlice — same key, same store.
    const cached = await getCached(STORES.LABELS, 'all');
    if (cached) return cached;

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(`${BASE_URL}/label/get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCached(STORES.LABELS, 'all', response.data, TTL.LABELS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to assign label
export const assignLabel = createAsyncThunk(
  "inboxInfo/assignLabel",
  async ({ labelName, contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/assign`,
        { labelName, contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to create and assign label
export const createAndAssignLabel = createAsyncThunk(
  "inboxInfo/createAndAssignLabel",
  async ({ labelName, contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/create-and-assign`,
        { labelName, contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to remove label from contact
export const removeLabelFromContact = createAsyncThunk(
  "inboxInfo/removeLabelFromContact",
  async ({ labelName, contactId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/label/remove`,
        { labelName, contactId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to add note to contact
export const addNote = createAsyncThunk(
  "inboxInfo/addNote",
  async ({ contactId, text }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/contacts/${contactId}/notes`,
        { text },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to update note
export const updateNote = createAsyncThunk(
  "inboxInfo/updateNote",
  async ({ contactId, noteId, text }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.put(
        `${BASE_URL}/contacts/${contactId}/notes/${noteId}`,
        { text },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to delete note
export const deleteNote = createAsyncThunk(
  "inboxInfo/deleteNote",
  async ({ contactId, noteId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}/contacts/${contactId}/notes/${noteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || error.message);
    }
  }
);

const inboxInfoSlice = createSlice({
  name: "inboxInfo",
  initialState: {
    contactInfo: null,
    groupInfo: null,
    labels: [],
    isLoading: false,
    isError: false,
    errorMessage: null,
  },
  reducers: {
    clearGroupInfo: (state) => {
      state.groupInfo = null;
    },
    clearContactInfo: (state) => {
      state.contactInfo = null;
      state.isLoading = false;
      state.isError = false;
      state.errorMessage = null;
    },
    clearErrors: (state) => {
      state.isError = false;
      state.errorMessage = null;
    },
    // Merges a few live-updated fields into the contact currently open in the inbox — only if that
    // is really the contact the update is for (a patch for contact A never touches contact B).
    // Used by useShopifyCustomerLinkSync; deliberately narrower than setContactInfo below.
    patchContactInfo: (state, action) => {
      const { contactId, changes } = action.payload || {};
      const activeId = state.contactInfo?._id || state.contactInfo?.contactId || state.contactInfo?.contactID;
      if (contactId && changes && activeId && String(activeId) === String(contactId)) {
        state.contactInfo = { ...state.contactInfo, ...changes };
      }
    },
    setContactInfo: (state, action) => {
      // CRITICAL: Filter out undefined values from payload to prevent overwriting existing values
      // This is especially important for internal mentions where assignment fields should be preserved
      const filteredPayload = Object.fromEntries(
        Object.entries(action.payload).filter(([_, value]) => value !== undefined)
      );
      
      let newContactInfo = {
        ...state.contactInfo,
        ...filteredPayload, // Use filtered payload instead of raw payload
        // IMPORTANT: Always update status if provided in payload
        ...(action.payload.status !== undefined && { status: action.payload.status }),
        // IMPORTANT: Always update assignment fields if provided
        ...(action.payload.assignedTo !== undefined && { assignedTo: action.payload.assignedTo }),
        ...(action.payload.assignedToName !== undefined && { assignedToName: action.payload.assignedToName }),
        ...(action.payload.assignedToId !== undefined && { assignedToId: action.payload.assignedToId }),
        ...(action.payload.labels !== undefined
          ? { labels: action.payload.labels }
          : { labels: state.contactInfo?.labels || [] }),
        ...(action.payload.hoursRemaining !== undefined && { hoursRemaining: action.payload.hoursRemaining }),
      };
      
      // If lastMessage is closechat, set status accordingly (but don't override if status already set)
      if (newContactInfo.lastMessage && newContactInfo.lastMessage.type === "closechat" && !action.payload.status) {
        newContactInfo.status = "Closed";
      }
      state.contactInfo = newContactInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Contact By ID
      .addCase(fetchContactById.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchContactById.fulfilled, (state, action) => {
        state.isLoading = false;
        let newContactInfo = action.payload;
        
        // Preserve hoursRemaining if it was recently set by socket events
        // This prevents API calls from overwriting real-time hours data
        if (state.contactInfo?.hoursRemaining !== undefined && 
            state.contactInfo?.hoursRemaining > 0 &&
            newContactInfo.hoursRemaining === 0) {
          newContactInfo.hoursRemaining = state.contactInfo.hoursRemaining;
        }
        
        // Ensure notes array is included (default to empty array if not present)
        if (!newContactInfo.notes) {
          newContactInfo.notes = state.contactInfo?.notes || [];
        }
        
        // If lastMessage is closechat, set status accordingly
        if (newContactInfo.lastMessage && newContactInfo.lastMessage.type === "closechat") {
          newContactInfo.status = "Closed";
        }
        state.contactInfo = newContactInfo;
      })
      .addCase(fetchContactById.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload !== "Debounced") {
          state.isError = true;
          state.errorMessage = action.payload;
        }
      })
      // Fetch Group By ID -- own pending/fulfilled/rejected trio, deliberately not merged into
      // fetchContactById's own (that one has 1:1-specific hoursRemaining-preservation/notes/
      // closechat-status logic that doesn't apply to a group).
      .addCase(fetchGroupById.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchGroupById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.groupInfo = action.payload;
      })
      .addCase(fetchGroupById.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload !== "Debounced") {
          state.isError = true;
          state.errorMessage = action.payload;
        }
      })
      // Background contact refresh (stale-while-revalidate for IDB hits)
      // Only updates Redux if the refreshed contact is still the active one,
      // so a background refresh for contact A doesn't overwrite contact B's info.
      .addCase(refreshContactBackground.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        const activeId = state.contactInfo?._id || state.contactInfo?.contactId || state.contactInfo?.contactID;
        if (activeId && String(activeId) === String(contactId)) {
          state.contactInfo = data;
        }
      })
      .addCase(refreshContactBackground.pending, () => { /* no loading spinner */ })
      .addCase(refreshContactBackground.rejected, () => { /* silent failure */ })

      // Fetch Labels
      .addCase(fetchLabels.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload && action.payload.labels) {
          state.labels = action.payload.labels;
        }
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload !== "Debounced") {
          state.isError = true;
          state.errorMessage = action.payload;
        }
      })

      // Assign Label
      .addCase(assignLabel.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(assignLabel.fulfilled, (state, action) => {
        state.isLoading = false;
        if (
          action.payload &&
          action.payload.contact &&
          action.payload.contact.labels
        ) {
          if (state.contactInfo) {
            state.contactInfo.labels = action.payload.contact.labels;
          }
        }
      })
      .addCase(assignLabel.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Create and Assign Label
      .addCase(createAndAssignLabel.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createAndAssignLabel.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.labels) {
          state.labels = action.payload.labels;
        }
        if (
          action.payload.contact &&
          action.payload.contact.labels &&
          state.contactInfo
        ) {
          state.contactInfo.labels = action.payload.contact.labels;
        }
      })
      .addCase(createAndAssignLabel.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Remove Label from Contact
      .addCase(removeLabelFromContact.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(removeLabelFromContact.fulfilled, (state, action) => {
        state.isLoading = false;
        if (
          action.payload.contact &&
          action.payload.contact.labels &&
          state.contactInfo
        ) {
          state.contactInfo.labels = action.payload.contact.labels;
        }
      })
      .addCase(removeLabelFromContact.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Add Note
      .addCase(addNote.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(addNote.fulfilled, (state, action) => {
        state.isLoading = false;
        if (
          action.payload.contact &&
          action.payload.contact.notes &&
          state.contactInfo
        ) {
          state.contactInfo.notes = action.payload.contact.notes;
        }
      })
      .addCase(addNote.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Update Note
      .addCase(updateNote.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        state.isLoading = false;
        if (
          action.payload.contact &&
          action.payload.contact.notes &&
          state.contactInfo
        ) {
          state.contactInfo.notes = action.payload.contact.notes;
        }
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Delete Note
      .addCase(deleteNote.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.isLoading = false;
        if (
          action.payload.contact &&
          action.payload.contact.notes &&
          state.contactInfo
        ) {
          state.contactInfo.notes = action.payload.contact.notes;
        }
      })
      .addCase(deleteNote.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export const { clearContactInfo, clearGroupInfo, clearErrors, setContactInfo, patchContactInfo } =
  inboxInfoSlice.actions;
export default inboxInfoSlice.reducer;
